/**
 * PortfolioFitBadge — renders the position-aware action the backend attaches to
 * a prediction/recommendation (see backend/analytics/portfolio_fit.py).
 *
 * A prediction engine forecasts a stock's *direction*; portfolio_fit decides
 * whether acting on it makes sense for THIS book. This badge makes that visible
 * so the UI never reads "buy more" into an already-overweight name — instead it
 * shows e.g. "OVERWEIGHT 25.8% · HOLD" with the reason on hover.
 */
import { clsx } from 'clsx'
import type { PortfolioFit } from '@/lib/types'

const ACTION_TONE: Record<string, string> = {
  BUY: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  ADD: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/25',
  HOLD: 'bg-sky-500/15 text-sky-300 border-sky-500/25',
  TRIM: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  EXIT: 'bg-rose-500/20 text-rose-300 border-rose-500/30',
  AVOID: 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30',
}

export function PortfolioFitBadge({ fit, compact = false, title }: { fit: PortfolioFit | null | undefined; compact?: boolean; title?: string | null }) {
  if (!fit || !fit.action) return null

  const tone = ACTION_TONE[fit.action] ?? 'bg-zinc-500/20 text-zinc-300 border-zinc-500/30'
  const weightLabel = fit.held ? `${(fit.current_weight_pct ?? 0).toFixed(1)}%` : 'not held'

  return (
    <span
      className={clsx('inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold', tone)}
      title={title || fit.action_reason || undefined}
    >
      {fit.overweight && <span className="font-bold">⚠ OVERWEIGHT</span>}
      <span>{fit.action}</span>
      {!compact && (
        <span className="font-mono font-normal opacity-80">· {weightLabel}</span>
      )}
    </span>
  )
}

/**
 * A fuller one-line explanation for detail cards — the badge plus the reason.
 * Renders nothing when there's no actionable fit.
 */
export function PortfolioFitNote({ fit }: { fit: PortfolioFit | null | undefined }) {
  if (!fit || !fit.action || !fit.action_reason) return null
  return (
    <div className="mt-1.5 flex items-start gap-1.5 text-[11px] text-text-muted">
      <PortfolioFitBadge fit={fit} compact />
      <span className="leading-snug">{fit.action_reason}</span>
    </div>
  )
}

export default PortfolioFitBadge
